/**
 * Chat attachments — inline images (Apr 2026) + generic files (May 2026).
 *
 * Images: vision-fed straight to the LLM via multimodal payload.
 * Files: persisted as `content_items` row with `source_type='manual_upload'`
 * + `conversation_id NOT NULL`. The `content_items.ingestion_status` column
 * defaults to `'skipped'` (storage-only), but `uploadChatFile` uploads with
 * an EXPLICIT `trigger_ingestion=true` (Jun 2026), so a chat-dropped file is
 * always queued for full RAG ingestion — its chip transitions
 * pending → processing → completed and the file becomes searchable +
 * section-navigable in later turns. (The column default only governs rows
 * created by other paths that opt out.)
 *
 * v1 ref: `ando-frontend/app/types/chat.ts:565-597`.
 */

import { z } from "zod";

/** Max bytes per attachment — enforced FE + BE. */
export const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;
/** Soft warning threshold — shows "large file" hint above this. */
export const WARN_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;

// Per-bucket limits (v1 useChatAttachments.ts:33-100)
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_COUNT = 4;
export const MAX_FILE_COUNT = 5;

export const SUPPORTED_IMAGE_MIME = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
] as const;

export const SUPPORTED_FILE_MIME = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
  "text/csv",
  "text/plain",
  "text/markdown",
  "text/html",
  // Archive (June 25, 2026): a .zip is expanded BE-side into its member files
  // (e.g. a month of meter CSVs), each re-ingested through the normal pipeline.
  // The BE intercepts zip before the per-file allowlist; the FE just needs to
  // let the user pick + upload it.
  "application/zip",
  "application/x-zip-compressed",
  // Audio (June 1, 2026 — composer voice-note button). Browsers emit
  // ``audio/webm;codecs=opus`` for MediaRecorder by default; we keep the
  // base MIME on the allowlist and let the browser-specific codec
  // suffix flow through (BE policy matches on base MIME).
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
  "audio/ogg",
  "audio/wav",
] as const;

/** True when the MIME identifies an audio attachment (voice note). */
export function isAudioMime(mime: string | null | undefined): boolean {
  if (!mime) return false;
  return mime.toLowerCase().startsWith("audio/");
}

/**
 * Strip MIME ``type/subtype`` from any RFC 7231 parameters (e.g.
 * codec suffix ``audio/webm;codecs=opus`` → ``audio/webm``). The
 * allowlists at both FE and BE store the base form so they can match
 * the variants browsers ship — Chrome's MediaRecorder always emits
 * a codec suffix, Safari sometimes adds ``;codecs="mp4a.40.2"``, etc.
 *
 * Returns the input lowercased + trimmed even when no parameter is
 * present, so call sites can compare canonically with one helper.
 */
export function baseMime(mime: string | null | undefined): string {
  if (!mime) return "";
  const semi = mime.indexOf(";");
  return (semi >= 0 ? mime.slice(0, semi) : mime).trim().toLowerCase();
}

export const ChatImageAttachmentSchema = z.object({
  minio_key: z.string(),
  filename: z.string(),
  mime_type: z.string(),
});

export type ChatImageAttachment = z.infer<typeof ChatImageAttachmentSchema>;

export const FileIngestionStatusSchema = z.enum([
  "skipped",
  "pending",
  "processing",
  "completed",
  "failed",
]);

export type FileIngestionStatus = z.infer<typeof FileIngestionStatusSchema>;

export const ChatFileAttachmentSchema = z.object({
  content_id: z.string(),
  filename: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  ingestion_status: FileIngestionStatusSchema,
  /** When the upload was a .zip expanded server-side, the number of member
   *  files extracted (so the chip can show "N file" instead of a size). */
  extracted_count: z.number().optional(),
  /** Member content_ids of an expanded .zip (capped). At send these REPLACE the
   *  archive's own (non-readable) handle so the agent receives the real member
   *  files via the normal ``fetch_chat_file_refs`` path — no phantom attachment,
   *  no separate mechanism. */
  extracted_ids: z.array(z.string()).optional(),
});

export type ChatFileAttachment = z.infer<typeof ChatFileAttachmentSchema>;
